
import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useFamilySettings } from './family/useFamilySettings';
import CreateFamilyDialog from './family/CreateFamilyDialog';
import InviteMemberDialog from './family/InviteMemberDialog';
import RemoveMemberDialog from './family/RemoveMemberDialog';
import FamilyErrorState from './family/FamilyErrorState';
import FamilyLoadingState from './family/FamilyLoadingState';
import EmptyFamilyState from './family/EmptyFamilyState';
import FamilyContent from './family/FamilyContent';
import { useAuth } from '@/context/AuthContext';
import { FamilyMember } from './family/types';

interface FamilySettingsProps {
  onError?: (error: string) => void;
}

const FamilySettings = ({ onError }: FamilySettingsProps) => {
  const { user } = useAuth();
  const [showCreateFamily, setShowCreateFamily] = useState(false);
  const [showInviteMember, setShowInviteMember] = useState(false);
  const [memberToRemove, setMemberToRemove] = useState<FamilyMember | null>(null);
  
  const {
    isLoading,
    family,
    members,
    error,
    createFamily,
    inviteFamilyMember,
    removeFamilyMember,
    handleRetry,
    fetchFamilyData
  } = useFamilySettings(onError);

  const handleCreateFamily = async (familyName: string) => {
    await createFamily(familyName);
    setShowCreateFamily(false);
  };

  const handleInviteMember = async (email: string) => {
    await inviteFamilyMember(email);
    setShowInviteMember(false);
  };

  const handleRemoveMember = async () => {
    if (memberToRemove) {
      await removeFamilyMember(memberToRemove);
      setMemberToRemove(null);
    }
  };
  
  return (
    <>
      <Card>
        <CardHeader className="text-center sm:text-left">
          <CardTitle>Family Sharing</CardTitle>
          <CardDescription>
            Create a family group to share memories with loved ones
          </CardDescription>
        </CardHeader>
        <CardContent>
          {error && (
            <FamilyErrorState 
              error={error} 
              onRetry={handleRetry} 
            />
          )}
          
          {isLoading ? (
            <FamilyLoadingState />
          ) : family ? (
            <FamilyContent 
              family={family}
              members={members}
              currentUserId={user?.id}
              onInviteMember={() => setShowInviteMember(true)}
              onRemoveMember={setMemberToRemove}
              onRefresh={fetchFamilyData}
            />
          ) : (
            <EmptyFamilyState 
              onCreateFamily={() => setShowCreateFamily(true)}
            />
          )}
        </CardContent>
      </Card>
      
      <CreateFamilyDialog
        open={showCreateFamily}
        onOpenChange={setShowCreateFamily}
        onCreateFamily={handleCreateFamily}
      />
      
      <InviteMemberDialog
        open={showInviteMember}
        onOpenChange={setShowInviteMember}
        onInviteMember={handleInviteMember}
      />
      
      <RemoveMemberDialog
        member={memberToRemove}
        onMemberChange={setMemberToRemove}
        onRemoveMember={handleRemoveMember}
      />
    </>
  );
};

export default FamilySettings;
